import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, HourglassMedium, Files, ChartBar, Warning, ArrowLeft, ArrowRight, MagnifyingGlass, X, DownloadSimple } from "@phosphor-icons/react";

const fmtInr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));
const fmtCompact = (n) => {
    const v = Number(n || 0);
    if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
    if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
    if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
    return `₹${v.toFixed(0)}`;
};

const istToday = () => {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
    const ist = new Date(utcMs + 5.5 * 60 * 60_000);
    const p = (n) => String(n).padStart(2, "0");
    return `${ist.getFullYear()}-${p(ist.getMonth() + 1)}-${p(ist.getDate())}`;
};
const istFirstOfMonth = () => {
    const t = istToday();
    return `${t.slice(0, 7)}-01`;
};

/**
 * v1.27.80 — Dedicated Slip Summary page. Full breakdown, 3-month
 * trend, per-company (Super User), Missing UTR + Pending Uploads
 * sections, and a date-range filter.
 */
export default function SlipSummary() {
    const [dateFrom, setDateFrom] = useState(istFirstOfMonth());
    const [dateTo, setDateTo] = useState(istToday());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { date_from: dateFrom, date_to: dateTo };
            const r = await api.get("/dashboard/monthly-slip-summary", { params });
            setData(r.data);
        } finally { setLoading(false); }
    }, [dateFrom, dateTo]);
    useEffect(() => { load(); }, [load]);

    // --- Drill-down (v1.31.17): clicking a tile lists its slips below ---
    const LABELS = { accepted: "Accepted", rejected: "Rejected", pending: "Pending", total: "All" };
    const [selected, setSelected] = useState(null);
    const [drill, setDrill] = useState(null);
    const [drillLoading, setDrillLoading] = useState(false);
    const [drillExporting, setDrillExporting] = useState(false);
    const fmtDate = (s) => { if (!s) return "—"; const parts = String(s).split("-"); return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : s; };

    const openDrill = async (status) => {
        setSelected(status); setDrill(null); setDrillLoading(true);
        try {
            const r = await api.get("/dashboard/slip-summary-detail", { params: { status, date_from: dateFrom, date_to: dateTo } });
            setDrill(r.data);
        } catch { setDrill({ rows: [], count: 0, total_amount: 0 }); }
        finally { setDrillLoading(false); }
    };
    const closeDrill = () => { setSelected(null); setDrill(null); };
    const exportDrill = async () => {
        setDrillExporting(true);
        try {
            const res = await api.get("/dashboard/slip-summary-detail.xlsx", { params: { status: selected, date_from: dateFrom, date_to: dateTo }, responseType: "blob" });
            const url = URL.createObjectURL(res.data); const a = document.createElement("a");
            a.href = url; a.download = `slip-${selected}-detail.xlsx`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } finally { setDrillExporting(false); }
    };
    // A date-range change invalidates any open drill-down.
    useEffect(() => { setSelected(null); setDrill(null); }, [dateFrom, dateTo]);

    const trendMax = Math.max(1, ...((data?.trend || []).map((t) => t.accepted_amount || 0)));
    const c = data?.current;
    const cards = c ? [
        { key: "accepted", label: "Accepted", icon: CheckCircle,     count: c.accepted.count, amount: c.accepted.amount, tone: "border-emerald-200 bg-emerald-50 text-emerald-900", accent: "text-emerald-700" },
        { key: "rejected", label: "Rejected", icon: XCircle,         count: c.rejected.count, amount: c.rejected.amount, tone: "border-rose-200 bg-rose-50 text-rose-900",        accent: "text-rose-700" },
        { key: "pending",  label: "Pending",  icon: HourglassMedium, count: c.pending.count,  amount: c.pending.amount,  tone: "border-amber-200 bg-amber-50 text-amber-900",     accent: "text-amber-700" },
        { key: "total",    label: "Total",    icon: Files,           count: c.total.count,    amount: c.total.amount,    tone: "border-slate-300 bg-slate-50 text-slate-900",     accent: "text-slate-700" },
    ] : [];

    return (
        <div className="space-y-5" data-testid="slip-summary-page">
            <div className="flex flex-wrap items-center gap-3">
                <Link to="/admin" className="text-slate-500 hover:text-slate-800"><ArrowLeft size={20} /></Link>
                <div className="flex-1">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Reports</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Slip Summary</h1>
                    <p className="text-xs text-slate-500">Full breakdown of accepted / rejected / pending slips with trend, per-company and gap sections.</p>
                </div>
            </div>

            {/* Date range */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">From</Label>
                        <Input type="date" data-testid="ss-date-from" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">To</Label>
                        <Input type="date" data-testid="ss-date-to" value={dateTo} min={dateFrom} max={istToday()} onChange={(e) => setDateTo(e.target.value)} className="mt-1" />
                    </div>
                    <div className="flex items-end">
                        <Button variant="outline" className="w-full" onClick={load} disabled={loading} data-testid="ss-refresh">{loading ? "Loading…" : "Refresh"}</Button>
                    </div>
                </div>
            </div>

            {/* Current breakdown */}
            {c && (
                <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="ss-current">
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="font-display text-lg font-bold text-slate-900">{data.heading}</h2>
                        <span className="text-[10px] uppercase tracking-widest text-slate-500">Period totals</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {cards.map(({ key, label, icon: Icon, count, amount, tone, accent }) => (
                            <button type="button" key={key} onClick={() => openDrill(key)} className={`rounded-lg border ${tone} p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none ${selected === key ? "ring-2 ring-offset-2 ring-slate-900 shadow-md" : ""}`} data-testid={`ss-card-${key}`}>
                                <div className="flex items-center justify-between">
                                    <div className={`text-[10px] font-bold uppercase tracking-[0.22em] ${accent}`}>{label}</div>
                                    <Icon size={14} weight="duotone" className={accent} />
                                </div>
                                <div className="mt-2 font-display text-2xl font-bold tabular-nums">{count}</div>
                                <div className="text-xs font-mono text-slate-600" title={fmtInr(amount)}>{fmtCompact(amount)}</div>
                                <div className="text-[10px] font-mono text-slate-400 truncate">{fmtInr(amount)}</div>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {/* Drill-down list — opens below the tiles on the same page */}
            {selected && (
                <section data-testid="ss-drilldown" className="rounded-xl border border-slate-300 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h2 className="font-display text-base font-bold text-slate-900" data-testid="ss-drilldown-heading">
                            {LABELS[selected]} Slips — {drill?.heading || `${fmtDate(dateFrom)} to ${fmtDate(dateTo)}`}
                            {drill ? <span className="ml-2 text-xs font-medium text-slate-500">({drill.count} · {fmtInr(drill.total_amount)})</span> : null}
                        </h2>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={exportDrill} disabled={drillExporting || !drill || drill.count === 0} data-testid="ss-drilldown-export"><DownloadSimple size={14} className="mr-1" /> {drillExporting ? "Preparing…" : "Export to Excel"}</Button>
                            <Button variant="ghost" size="sm" onClick={closeDrill} data-testid="ss-drilldown-close"><X size={14} className="mr-1" /> Close</Button>
                        </div>
                    </div>
                    {drillLoading ? (
                        <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
                    ) : !drill || drill.rows.length === 0 ? (
                        <div className="py-8 text-center text-sm text-slate-400" data-testid="ss-drilldown-empty">No slips found in this range.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                                    <tr>
                                        <th className="px-3 py-2 font-medium">Date</th>
                                        <th className="px-3 py-2 font-medium">Party Name</th>
                                        <th className="px-3 py-2 font-medium">UTR</th>
                                        <th className="px-3 py-2 text-right font-medium">Amount</th>
                                        {selected === "total" && <th className="px-3 py-2 font-medium">Status</th>}
                                        {(selected === "accepted" || selected === "total") && <th className="px-3 py-2 font-medium">Verified By</th>}
                                        {(selected === "rejected" || selected === "total") && <th className="px-3 py-2 font-medium">Rejection Reason</th>}
                                        {(selected === "pending" || selected === "total") && <th className="px-3 py-2 font-medium">Uploaded By</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {drill.rows.map((r, i) => (
                                        <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid="ss-drilldown-row">
                                            <td className="px-3 py-2 text-slate-600">{fmtDate(r.date)}</td>
                                            <td className="px-3 py-2 font-medium text-slate-900">{r.party_name}</td>
                                            <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.utr || "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums text-slate-900">{fmtInr(r.amount)}</td>
                                            {selected === "total" && <td className="px-3 py-2 text-slate-700">{r.status}</td>}
                                            {(selected === "accepted" || selected === "total") && <td className="px-3 py-2 text-slate-600">{r.verified_by || "—"}</td>}
                                            {(selected === "rejected" || selected === "total") && <td className="px-3 py-2 text-slate-600">{r.rejection_reason || "—"}</td>}
                                            {(selected === "pending" || selected === "total") && <td className="px-3 py-2 text-slate-600">{r.uploaded_by || "—"}</td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}

            {/* Trend */}
            {data?.trend?.length > 0 && (
                <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="ss-trend">
                    <div className="mb-3 flex items-center gap-2">
                        <ChartBar size={14} weight="duotone" className="text-slate-500" />
                        <h2 className="font-display text-base font-bold text-slate-900">Last 3 Months · Accepted</h2>
                    </div>
                    <div className="flex items-end justify-around gap-2">
                        {data.trend.map((t) => {
                            const h = Math.round((t.accepted_amount / trendMax) * 90) || 3;
                            const monthISO = `${t.year}-${String(t.month).padStart(2, "0")}`;
                            return (
                                <Link
                                    key={monthISO}
                                    to={`/admin/uploads?month=${monthISO}`}
                                    className="group flex flex-1 flex-col items-center gap-1 rounded-lg p-1 transition-colors hover:bg-slate-50"
                                    data-testid={`ss-trend-${monthISO}`}
                                    title={`Open ${t.label} uploads`}
                                >
                                    <div className="text-[10px] font-mono font-semibold tabular-nums text-slate-700">{fmtCompact(t.accepted_amount)}</div>
                                    <div className="flex h-[90px] w-full items-end justify-center">
                                        <div className="w-10 rounded-t-md bg-gradient-to-t from-emerald-600 to-emerald-400 shadow-sm transition-all group-hover:-translate-y-0.5" style={{ height: `${h}px` }} />
                                    </div>
                                    <div className="text-center">
                                        <div className="text-[11px] font-semibold text-slate-800">{t.label}</div>
                                        <div className="text-[10px] text-slate-500">{t.accepted_count} slip{t.accepted_count === 1 ? "" : "s"}</div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Missing UTR */}
            {data?.missing_utr && (
                <section className="rounded-xl border border-rose-200 bg-rose-50/60 p-4 shadow-sm" data-testid="ss-missing-utr">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <Warning size={18} weight="fill" className="text-rose-600" />
                            <div>
                                <h2 className="font-display text-base font-bold text-rose-900">Missing UTR</h2>
                                <div className="text-[11px] text-rose-700">Verified slips saved without a UTR number.</div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="font-display text-2xl font-bold tabular-nums text-rose-800" data-testid="ss-missing-utr-count">{data.missing_utr.count}</div>
                            <div className="text-xs font-mono text-rose-700" title={fmtInr(data.missing_utr.amount)}>{fmtCompact(data.missing_utr.amount)}</div>
                        </div>
                        <Link to="/admin/uploads?status=verified&missing_utr=1" className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-rose-700" data-testid="ss-missing-utr-link">
                            Backfill now <ArrowRight size={12} weight="bold" />
                        </Link>
                    </div>
                </section>
            )}

            {/* Pending Uploads */}
            {data?.pending_uploads_summary && (
                <section className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 shadow-sm" data-testid="ss-pending">
                    <div className="mb-3 flex items-center gap-2">
                        <HourglassMedium size={16} weight="fill" className="text-amber-600" />
                        <h2 className="font-display text-base font-bold text-amber-900">Pending Uploads</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border border-amber-200 bg-white p-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Today</div>
                            <div className="mt-1 font-display text-xl font-bold tabular-nums text-slate-900">{data.pending_uploads_summary.today.count}</div>
                            <div className="text-xs font-mono text-slate-600" title={fmtInr(data.pending_uploads_summary.today.amount)}>{fmtCompact(data.pending_uploads_summary.today.amount)}</div>
                        </div>
                        <div className="rounded-lg border border-amber-300 bg-white p-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">All time pending</div>
                            <div className="mt-1 font-display text-xl font-bold tabular-nums text-amber-900">{data.pending_uploads_summary.all_time.count}</div>
                            <div className="text-xs font-mono text-amber-800" title={fmtInr(data.pending_uploads_summary.all_time.amount)}>{fmtCompact(data.pending_uploads_summary.all_time.amount)}</div>
                        </div>
                    </div>
                    <div className="mt-3">
                        <Link to="/admin/uploads?status=pending" className="text-xs font-semibold text-amber-800 hover:underline" data-testid="ss-pending-link">Open pending queue →</Link>
                    </div>
                </section>
            )}

            {/* Super User per-company */}
            {data?.is_super && data?.by_company?.length > 0 && (
                <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ss-by-company">
                    <div className="border-b border-slate-100 px-4 py-3">
                        <h2 className="font-display text-base font-bold text-slate-900">Company breakdown</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 font-medium">Company</th>
                                    <th className="px-3 py-2 text-right font-medium text-emerald-700">✓ Accepted</th>
                                    <th className="px-3 py-2 text-right font-medium text-rose-700">✕ Rejected</th>
                                    <th className="px-3 py-2 text-right font-medium text-amber-700">⋯ Pending</th>
                                    <th className="px-3 py-2 text-right font-medium">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_company.map((cc) => (
                                    <tr key={cc.company_id} className="border-t border-slate-100" data-testid={`ss-company-${cc.company_id}`}>
                                        <td className="px-3 py-2 font-medium text-slate-800">{cc.company_name}</td>
                                        <td className="px-3 py-2 text-right tabular-nums"><div className="font-semibold text-emerald-800">{cc.accepted.count}</div><div className="text-[9px] text-slate-500">{fmtCompact(cc.accepted.amount)}</div></td>
                                        <td className="px-3 py-2 text-right tabular-nums"><div className="font-semibold text-rose-800">{cc.rejected.count}</div><div className="text-[9px] text-slate-500">{fmtCompact(cc.rejected.amount)}</div></td>
                                        <td className="px-3 py-2 text-right tabular-nums"><div className="font-semibold text-amber-800">{cc.pending.count}</div><div className="text-[9px] text-slate-500">{fmtCompact(cc.pending.amount)}</div></td>
                                        <td className="px-3 py-2 text-right tabular-nums"><div className="font-semibold text-slate-900">{cc.total.count}</div><div className="text-[9px] text-slate-500">{fmtCompact(cc.total.amount)}</div></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
}
